import * as React from 'react';


const storage = ({
  setValue: <T>(key: string, value: T): boolean => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
      return true;

    } catch (ex) {
      console.log(`Failed to set session storage value for key (${key}):`, ex);
      return false;
    }
  },

  getValue: <T>(key: string): T | undefined => {
    try {
      const serialized = sessionStorage.getItem(key);

      if (serialized === null || serialized === undefined) {
        return undefined;
      }

      return JSON.parse(serialized) as T;

    } catch (ex) {
      console.log(`Failed to get session storage value for key (${key}):`, ex);
      return undefined;
    }
  },

  removeValue: (key: string): void => {
    try {
      sessionStorage.removeItem(key);
    } catch (ex) {
      console.log(`Failed to remove session storage value for key (${key}):`, ex);
    }
  },
});


/**
 * A hook that interfaces with session storage. Does not update if session storage is updated through some means other
 * than this hook.
 */
export const useSessionStorage = <T>(
  key: string,
  defaultValue?: T,
): [T | undefined, (obj: T) => void, () => void] => {
  const [value, setValue] = React.useState<T | undefined>(() => storage.getValue(key) ?? defaultValue);

  const set = React.useCallback(
    (value: T) => storage.setValue(key, value) && setValue(value),
    [key, setValue],
  );

  const remove = React.useCallback(() => storage.removeValue(key), [key]);

  return React.useMemo(() => [value, set, remove], [value, set, remove]);
};
